import React, { useEffect, useMemo, useState } from 'react';
import axios from '../config/api';
import { sortNewestFirst } from '../utils/newestFirst';
import {
  FaShieldAlt,
  FaUserPlus,
  FaUsers,
} from 'react-icons/fa';

export type UserSectionId =
  | 'add-users'
  | 'assign-roles'
  | 'edit-manage'
  | 'security-access';

export const USERS_NAV_ITEMS: { id: UserSectionId; label: string }[] = [
  { id: 'add-users', label: 'Add users' },
  { id: 'assign-roles', label: 'Assign roles' },
  { id: 'edit-manage', label: 'Edit or manage users' },
  { id: 'security-access', label: 'Security & access' },
];

interface UsersInfoPanelProps {
  activeSection?: UserSectionId;
  selectedCafe?: string;
}

export default function UsersInfoPanel({
  selectedCafe = '',
}: UsersInfoPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [team, setTeam] = useState<any[]>([]);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'staff' as 'admin' | 'manager' | 'staff',
    is_active: true,
  });

  const canCreate = useMemo(
    () =>
      !!selectedCafe &&
      !!form.name.trim() &&
      !!form.email.trim() &&
      form.password.trim().length >= 8,
    [selectedCafe, form]
  );

  const loadTeam = async () => {
    try {
      setLoading(true);
      const url = selectedCafe ? `/api/barista/team?cafe_id=${selectedCafe}` : '/api/barista/team';
      const { data } = await axios.get<any[]>(url);
      setTeam(sortNewestFirst(data || [], (member) => member.created_at));
    } catch (e) {
      setTeam([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeam();
  }, [selectedCafe]);

  const createUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreate) return;
    try {
      setSaving(true);
      await axios.post('/api/barista/team', {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        password: form.password,
        role: form.role,
        cafe_id: selectedCafe,
      });
      setForm({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: 'staff',
        is_active: true,
      });
      await loadTeam();
    } finally {
      setSaving(false);
    }
  };

  const updateUser = async (id: string, next: any) => {
    await axios.put(`/api/barista/team/${id}`, {
      role: next.role,
      is_active: !!next.is_active,
      name: next.name,
      phone: next.phone || '',
    });
    await loadTeam();
  };

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <h2 className="text-lg font-bold text-amber-800 mb-4 flex items-center gap-2">
          <FaUserPlus className="text-amber-700" />
          Team setup (single page)
        </h2>
        {!selectedCafe && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded p-3 mb-4">
            Select a cafe above to create users for that location.
          </p>
        )}
        <form className="grid md:grid-cols-2 gap-3" onSubmit={createUser}>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Full name</label>
            <input className="w-full px-3 py-2 border rounded" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
            <input type="email" className="w-full px-3 py-2 border rounded" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Phone</label>
            <input className="w-full px-3 py-2 border rounded" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Temporary password</label>
            <input
              type="password"
              minLength={8}
              maxLength={128}
              className="w-full px-3 py-2 border rounded"
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            />
            <p className="text-xs text-gray-500 mt-1">At least 8 characters.</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Role</label>
            <select className="w-full px-3 py-2 border rounded" value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value as any }))}>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="staff">Staff</option>
            </select>
          </div>
          <div className="flex items-end justify-end">
            <button disabled={!canCreate || saving} className="px-4 py-2 bg-amber-700 text-white rounded disabled:opacity-50">
              {saving ? 'Creating...' : 'Create user'}
            </button>
          </div>
        </form>
      </section>

      <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <h2 className="text-lg font-bold text-amber-800 mb-4 flex items-center gap-2">
          <FaUsers className="text-amber-700" />
          Team members
        </h2>
        <p className="text-xs text-gray-500 mb-3">Newest accounts first.</p>
        {loading ? (
          <p className="text-sm text-gray-500">Loading team...</p>
        ) : team.length === 0 ? (
          <p className="text-sm text-gray-500">No users yet for this scope.</p>
        ) : (
          <div className="space-y-3">
            {team.map((u) => (
              <div key={u.id} className="border rounded-lg p-3 flex flex-wrap items-end gap-3 justify-between">
                <div className="min-w-[220px]">
                  <p className="font-semibold text-gray-900">{u.name}</p>
                  <p className="text-xs text-gray-500">{u.email}</p>
                  <p className="text-xs text-gray-500">{u.phone || 'No phone'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    className="px-2 py-1 border rounded text-sm"
                    value={u.role || 'staff'}
                    onChange={(e) => updateUser(u.id, { ...u, role: e.target.value })}
                  >
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="staff">Staff</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => updateUser(u.id, { ...u, is_active: !u.is_active })}
                    className={`px-3 py-1 rounded text-xs font-semibold ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}
                  >
                    {u.is_active ? 'Active' : 'Disabled'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <h2 className="text-lg font-bold text-amber-800 mb-4 flex items-center gap-2">
          <FaShieldAlt className="text-amber-700" />
          Security & access
        </h2>
        <p className="text-sm text-gray-700">
          Keep only trusted people as Admin. Review team access regularly and disable accounts that no longer need access.
        </p>
      </section>
    </div>
  );
}
